import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import NotificationBell from '../ui/NotificationBell';
import Modal from '../ui/Modal';

interface DashboardLayoutProps {
  children: React.ReactNode;
  showBack?: boolean;
  backLabel?: string;
  backTo?: string;
  onEditProfile?: () => void;
  onStartTour?: () => void;
}

export default function DashboardLayout({
  children,
  showBack = false,
  backLabel = '← Back to Dashboard',
  backTo = '/dashboard',
  onEditProfile,
  onStartTour,
}: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const initials = user?.name
    ? user.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  const creditsDisplay = (user?.credits_remaining ?? 0).toLocaleString();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEditProfile = () => {
    setDropdownOpen(false);
    onEditProfile?.();
  };

  const handleSettings = () => {
    setDropdownOpen(false);
    navigate('/settings');
  };

  const handleLogoutClick = () => {
    setDropdownOpen(false);
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    logout();
  };

  return (
    <div className="min-h-screen bg-bg">
      {/* NAV */}
      <nav className="bg-card sticky top-0 z-40 border-b border-border">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2 sm:gap-4">
          {/* Left */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {showBack ? (
              <Link
                to={backTo}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card hover:bg-bg text-ink text-xs sm:text-sm font-semibold transition-all active:scale-95 shadow-sm"
                aria-label="Go back"
              >
                <svg className="h-4 w-4 text-ink flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>{backLabel.replace('← ', '') || 'Back'}</span>
              </Link>
            ) : (
              <>
                <Link to="/dashboard" className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                  <img src="/Eextramile1.png" alt="" className="h-7 w-auto sm:h-10 object-contain flex-shrink-0" />
                  <span className="font-bold text-ink text-sm sm:text-base tracking-tight truncate">Eextramile <span className="text-g">AI</span></span>
                </Link>
                <span className="text-border hidden sm:inline">·</span>
                <Link
                  to="/email-finder"
                  className="hidden sm:inline text-sm font-medium text-muted hover:text-ink transition-colors"
                >
                  Email Finder
                </Link>
              </>
            )}
          </div>

          {/* Right */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Credits pill — interactive link to pricing */}
            <Link
              to="/pricing"
              id="tour-credits"
              className="flex items-center px-2.5 sm:px-3 py-1.5 rounded-pill text-xs font-semibold bg-g-light border border-g-border text-g-text whitespace-nowrap hover:bg-emerald-100 hover:border-emerald-300 transition-colors cursor-pointer"
              title="View plans & buy credits"
            >
              {creditsDisplay} Credits
            </Link>

            {/* Notifications */}
            <NotificationBell />

            {/* Avatar + dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(v => !v)}
                className="group"
                title="Account"
              >
                <div className="h-8 w-8 rounded-full bg-g flex items-center justify-center text-white text-xs font-bold transition-opacity group-hover:opacity-75">
                  {initials}
                </div>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-card shadow-lg py-1 z-50">
                  <div className="px-3 py-2 border-b border-border mb-1">
                    <p className="text-xs font-semibold text-ink truncate">{user?.name ?? '—'}</p>
                    <p className="text-[11px] text-hint truncate">{user?.email ?? '—'}</p>
                  </div>
                  {/* Email Finder link for mobile viewports */}
                  <Link
                    to="/email-finder"
                    onClick={() => setDropdownOpen(false)}
                    className="sm:hidden w-full text-left px-3 py-2 text-sm text-ink hover:bg-bg transition-colors flex items-center gap-2"
                  >
                    <svg className="h-3.5 w-3.5 text-hint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Email Finder
                  </Link>
                  {onEditProfile && (
                    <button
                      onClick={handleEditProfile}
                      className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-bg transition-colors flex items-center gap-2"
                    >
                      <svg className="h-3.5 w-3.5 text-hint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Edit Profile
                    </button>
                  )}
                  <Link
                    to="/pricing"
                    onClick={() => setDropdownOpen(false)}
                    className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-bg transition-colors flex items-center gap-2"
                  >
                    <svg className="h-3.5 w-3.5 text-hint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    Plans & Pricing
                  </Link>
                  <button
                    onClick={handleSettings}
                    className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-bg transition-colors flex items-center gap-2"
                  >
                    <svg className="h-3.5 w-3.5 text-hint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings
                  </button>
                  {onStartTour && (
                    <button
                      onClick={() => { setDropdownOpen(false); onStartTour(); }}
                      className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-bg transition-colors flex items-center gap-2"
                    >
                      <svg className="h-3.5 w-3.5 text-hint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Take the tour
                    </button>
                  )}
                  <div className="border-t border-border mt-1 pt-1">
                    <button
                      onClick={handleLogoutClick}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Log out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* MAIN */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {children}
      </main>

      {/* Log out Confirmation Modal */}
      <Modal open={showLogoutModal} onClose={() => setShowLogoutModal(false)}>
        <div className="p-6">
          <div className="h-10 w-10 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mb-3">
            <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-ink mb-1">Log out of EextraMile?</h3>
          <p className="text-sm text-muted mb-5">
            You will need to sign in again to access your outreach pipeline.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowLogoutModal(false)}
              className="flex-1 py-2 rounded-input text-sm font-medium text-muted border border-border hover:text-ink hover:bg-bg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmLogout}
              className="flex-1 py-2 rounded-input text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
            >
              Log out
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
